'use client';

import { useState, useEffect, useCallback } from 'react';
import { SHA512 } from 'crypto-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Upload, X, Sparkles, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sha512HashContent } from '../../data/sha512-hash-content';

const sampleText = 'Hello, world! This is a sample text for SHA-512 hashing.';
const sampleHash = '2c74fd8edf1a9a7240d6f1c4e5c5e8b2c9f9a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b0';

export default function SHA512HashTool() {
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const { toast } = useToast();

    // Generate SHA-512 hash when input changes
    const generateHash = useCallback((text: string) => {
        if (!text.trim()) {
            setHash('');
            return;
        }

        try {
            setIsLoading(true);
            const hash = SHA512(text).toString();
            setHash(hash);
        } catch (error) {
            console.error('Error generating SHA-512 hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate SHA-512 hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        generateHash(input);
    }, [input, generateHash]);

    const handleCopy = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash)
            .then(() => {
                setIsCopied(true);
                toast({
                    title: 'Success',
                    description: 'Hash copied to clipboard!',
                });
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch(() => {
                toast({
                    title: 'Error',
                    description: 'Failed to copy to clipboard',
                    variant: 'destructive',
                });
            });
    };

    const handleClear = () => {
        setInput('');
        setFileName(null);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.onerror = () => {
            toast({
                title: 'Error',
                description: 'Failed to read file',
                variant: 'destructive',
            });
        };
        reader.readAsText(file);
    };

    const handleUseSample = () => {
        setInput(sampleText);
        setFileName(null);
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-4xl">
            <Toaster />
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <ToolPageIntro title="SHA-512 Hash Generator">
                Generate SHA-512 hashes from text or files. Strong cryptographic hashing for security-sensitive use cases. Paste or upload; instant results.
            </ToolPageIntro>

                {/* Main content */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Input Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>Input</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUseSample}
                                        disabled={isLoading}
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input || isLoading}
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <CardDescription>
                                {fileName ? (
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <FileText className="h-4 w-4 mr-2" />
                                        {fileName}
                                    </div>
                                ) : (
                                    'Enter text or upload a file to generate SHA-512 hash'
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="relative">
                                    <Textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[200px] font-mono text-sm"
                                        disabled={isLoading}
                                    />
                                    <div className="absolute right-2 bottom-2">
                                        <input
                                            type="file"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="file-upload"
                                            disabled={isLoading}
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                            className="h-8"
                                            disabled={isLoading}
                                        >
                                            <label htmlFor="file-upload" className="cursor-pointer">
                                                <Upload className="h-4 w-4 mr-2" />
                                                {fileName ? 'Change File' : 'Upload File'}
                                            </label>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Output Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle>SHA-512 Hash</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!hash || isLoading}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                            <CardDescription>
                                {hash ? 'Your SHA-512 hash is ready' : 'Hash will appear here'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className={cn(
                                'relative rounded-md border p-4 font-mono text-sm overflow-x-auto',
                                'min-h-[200px] flex items-center justify-center',
                                hash ? 'bg-muted/50' : 'bg-muted/20',
                                isLoading && 'opacity-70'
                            )}>
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center space-y-2 text-muted-foreground">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        <p>Generating hash...</p>
                                    </div>
                                ) : hash ? (
                                    <pre className="whitespace-pre-wrap break-all">{hash}</pre>
                                ) : (
                                    <p className="text-muted-foreground text-center">
                                        {input ? 'Processing...' : 'Enter text or upload a file to generate SHA-512 hash'}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Information Section */}
                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle>About SHA-512 Hash</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                            <p>
                                The SHA-512 (Secure Hash Algorithm 512-bit) is a member of the SHA-2 cryptographic hash functions designed by the NSA.
                                It produces a 512-bit (64-byte) hash value, typically rendered as a 128-character hexadecimal number.
                                SHA-512 is one of the most secure hashing functions available and is part of the SHA-2 family.
                            </p>
                            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 my-4 rounded-r">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-green-700 dark:text-green-300">
                                            <strong>Security Note:</strong> SHA-512 is currently considered highly secure and is recommended for applications requiring strong cryptographic security.
                                            It provides excellent resistance against collision attacks and is widely used in security-critical applications.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium mb-2">Common Uses</h3>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Secure password storage
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Digital signatures
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Certificate authorities
                                </li>
                                <li className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Cryptographic protocols
                                </li>
                            </ul>
                        </div>

                        <div className="mt-4 p-4 bg-muted/50 rounded-md">
                            <h4 className="text-sm font-medium mb-2">Example</h4>
                            <div className="space-y-2">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Input:</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">{sampleText}</pre>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">SHA-512 Hash (truncated):</p>
                                    <pre className="bg-background p-2 rounded text-xs overflow-x-auto">
                                        {sampleHash.substring(0, 64)}...{sampleHash.substring(sampleHash.length - 16)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={sha512HashContent.title}
                        description={sha512HashContent.description}
                        content={sha512HashContent.content}
                        faqs={sha512HashContent.faqs}
                        relatedTools={sha512HashContent.relatedTools}
                    />
                </div>
            </div>
            <Toaster />
        </div>
    );
}